import { getSocketUrl } from "../config/env";
import { SlotWebSocketClient } from "./slotWebSocketClient";
import { getSessionStorage } from "../utils/sessionStorageHelper";
import { igbSendMessage } from "../utils/postMessageHelper";
import { IWebSocketHandler } from "./socket";
import { PingPongService } from "./pingPongService";
import { logout, UserInfo } from "../auth/authService";
import {
  NetworkGameCycle,
  networkGameCycle,
  NetworkStatus,
  networkStatus,
} from "../store/networkStore";

export class SocketService {
  private webSocket?: SlotWebSocketClient;
  private pingPongService?: PingPongService;

  private static readonly SOCKET_MESSAGE_TYPES = {
    CONNECT: "CONNECT",
    CONNECTED: "CONNECTED",
    PONG: "PONG",
    MESSAGE: "MESSAGE",
    AUTHORIZED: "AUTHORIZED",
    UNAUTHORIZED: "UNAUTHORIZED",
    KICK: "KICK",
  } as const;

  public init() {
    this.connectSocket();
  }

  /**
   * WebSocket 서버 연결 시도
   */
  private connectSocket(): void {
    const userInfo = getSessionStorage("slotUserInfo");
    if (!userInfo) {
      throw new Error("User information not found");
    }

    const url = this.buildSocketUrl(userInfo);
    const handler = this.createSocketHandler(userInfo);

    this.webSocket = new SlotWebSocketClient();
    this.webSocket.connect({ url, handler });
  }

  /**
   * WebSocket URL 생성
   */
  private buildSocketUrl(userInfo: UserInfo): string {
    const baseUrl = getSocketUrl();
    const params = new URLSearchParams({
      playerId: userInfo.playerId,
      sessionKey: userInfo.sessionKey,
    });
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * WebSocket 핸들러 생성
   */
  private createSocketHandler(userInfo: UserInfo): IWebSocketHandler {
    return {
      onConnected: () => this.handleConnected(userInfo),
      onResponse: (data) => this.handleResponse(data),
      onSocketError: (error) => this.handleError(error),
      onSocketClose: () => this.handleClose(),
    };
  }

  /**
   * 연결 성공 시 처리
   * PingPong 서비스 시작 및 초기 메시지 전송
   */
  private handleConnected(userInfo: UserInfo): void {
    if (!this.webSocket) return;

    this.sendConnectMessage(userInfo);
    this.startPingPongService();
    this.notifyConnectionSuccess(userInfo);
  }

  /**
   * 서버에 CONNECT 메시지 전송
   */
  private sendConnectMessage(userInfo: UserInfo): void {
    const payload = JSON.stringify({
      playerId: userInfo.playerId,
      sessionKey: userInfo.sessionKey,
    });
    this.webSocket?.send("CONNECT", payload);
  }

  /**
   * PingPong 서비스 시작
   */
  private startPingPongService(): void {
    if (this.webSocket) {
      this.pingPongService = new PingPongService(this.webSocket);
    }
  }

  /**
   * 클라이언트에 연결 성공 알림
   */
  private notifyConnectionSuccess(userInfo: UserInfo): void {
    igbSendMessage("CONNECTED", {
      playerId: userInfo.playerId,
      sessionKey: userInfo.sessionKey,
      playerName: userInfo.playerName,
      playerStatus: userInfo.playerStatus?.toString(),
      assets: userInfo.assets,
    });
  }

  /**
   * 에러 처리
   * @param error 발생한 에러
   */
  private handleError(error: unknown): void {
    console.error("Socket service error:", error);
  }

  /**
   * 연결 종료 처리
   */
  public async handleClose(): Promise<void> {
    console.log("Socket service close");
    this.pingPongService?.stopPingInterval();
  }

  /**
   * 서버 응답 처리
   * @param data 서버로부터 받은 데이터
   */
  private handleResponse(data: unknown): void {
    try {
      if (!data || typeof data !== "object" || !("type" in data)) {
        return;
      }

      const { type, payload } = data as { type: string; payload: string };
      let parsed: unknown = undefined;
      try {
        parsed = payload ? JSON.parse(payload) : undefined;
      } catch (e) {
        console.warn("Failed to parse socket payload:", e, payload);
      }

      switch (type) {
        case SocketService.SOCKET_MESSAGE_TYPES.AUTHORIZED: {
          console.log("AUTHORIZED");
          break;
        }
        case SocketService.SOCKET_MESSAGE_TYPES.UNAUTHORIZED: {
          console.log("UNAUTHORIZED");
          break;
        }
        case SocketService.SOCKET_MESSAGE_TYPES.PONG: {
          this.handlePong(parsed as { serverTime?: number } | undefined);
          break;
        }
        case SocketService.SOCKET_MESSAGE_TYPES.KICK: {
          this.handleKick(parsed);
          break;
        }
        default: {
          // 알 수 없는 타입은 로깅만
          console.debug("Unhandled socket message:", type, parsed);
          break;
        }
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  private handlePong(data: { serverTime?: number } | undefined): void {
    if (data && typeof data.serverTime === "number") {
      igbSendMessage("TIME_UPDATE", { serverTime: data.serverTime }, false);
    }
  }

  private handleKick(data: any): void {
    const isGraceful = data.type === "GRACEFUL";
    const isIdle = networkGameCycle.get() === NetworkGameCycle.IDLE;
    if (isGraceful && !isIdle) {
      networkStatus.set(NetworkStatus.GRACEFUL_KICK);
      return;
    }
    igbSendMessage("SOCKET_FORCE_KICK");
    logout();
  }
}
